import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, X } from 'lucide-react';
import {
  BADGE_LABELS,
  DIVISION_COLORS,
  ECOSYSTEM_FILTERS,
  getEcosystemItems,
  ITEM_TYPE_LABELS,
  PURCHASE_ACTIONS,
  RELATIONSHIP_LABELS,
} from '@/data/ecosystem';
import { useLiveTick } from '@/lib/liveContent';
import { Reveal } from '../Reveal';
import { cn } from '@/lib/utils';

const enquiryHref = (item) => {
  const division = ['operations', 'people', 'growth', 'launch'].includes(item.serviceDivision)
    ? `service=${item.serviceDivision}&`
    : '';
  return `/?${division}source=Ecosystem#solution-finder`;
};

const actionFor = (item) => PURCHASE_ACTIONS[item.purchaseMode] || PURCHASE_ACTIONS.contact_us;

const CatalogueCard = ({ item, index }) => {
  const division = DIVISION_COLORS[item.serviceDivision] || DIVISION_COLORS.amden;
  const action = actionFor(item);
  const href =
    action.kind === 'details'
      ? item.href
      : action.kind === 'external'
        ? item.externalCheckoutUrl || item.href
        : enquiryHref(item);

  return (
    <Reveal delay={(index % 3) * 0.06} className="h-full">
      <article
        data-testid={`eco-card-${item.slug}`}
        data-item-type={item.itemType}
        data-purchase-mode={item.purchaseMode}
        data-billing-type={item.billingType}
        data-purchasable={item.price !== null}
        className="group flex h-full flex-col rounded-xl border border-slate-200 bg-white p-6 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-lift"
        style={{ borderTopWidth: '3px', borderTopColor: division.color }}
      >
        <div className="flex flex-wrap items-center gap-2">
          <span
            className="rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide"
            style={{ backgroundColor: division.soft, color: division.dark }}
          >
            {ITEM_TYPE_LABELS[item.itemType]}
          </span>
          <span className="rounded-full bg-navy-900 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
            {RELATIONSHIP_LABELS[item.relationship]}
          </span>
          {item.badge && BADGE_LABELS[item.badge] && (
            <span
              data-testid={`eco-badge-${item.slug}`}
              className="rounded-full bg-gold-600 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-navy-950"
            >
              {BADGE_LABELS[item.badge]}
            </span>
          )}
        </div>

        <h3 className="mt-4 text-lg font-extrabold leading-snug tracking-tight text-navy-900">
          {item.href ? (
            <Link
              to={item.href}
              data-testid={`eco-title-${item.slug}`}
              className="rounded-sm underline-offset-4 transition-colors hover:text-gold-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900"
            >
              {item.name}
            </Link>
          ) : (
            item.name
          )}
        </h3>
        <p className="mt-1 text-xs font-bold uppercase tracking-[0.14em]" style={{ color: division.dark }}>
          {division.label}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">{item.shortDescription}</p>

        {item.suitableFor && (
          <p className="mt-3 text-xs leading-relaxed text-slate-500">
            <span className="font-bold text-navy-800">Suitable for: </span>
            {item.suitableFor}
          </p>
        )}

        <div className="mt-auto pt-5">
          {item.format && (
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">{item.format}</p>
          )}
          {item.priceDisplay && (
            <p className="mb-3 text-lg font-extrabold text-navy-900">{item.priceDisplay}</p>
          )}
          <Link
            to={href}
            data-testid={`eco-action-${item.slug}`}
            className={cn(
              'group/btn inline-flex w-full items-center justify-center gap-2 rounded-md px-5 py-3 text-sm font-bold transition-colors',
              action.kind === 'details'
                ? 'border-2 border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white'
                : 'bg-navy-900 text-white hover:bg-navy-700'
            )}
          >
            {action.label}
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover/btn:translate-x-0.5" aria-hidden="true" />
          </Link>
        </div>
      </article>
    </Reveal>
  );
};

const matchesQuery = (item, q) => {
  const haystack = [item.name, item.shortDescription, item.provider, item.format, ITEM_TYPE_LABELS[item.itemType]]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return q.split(/\s+/).every((term) => haystack.includes(term));
};

export const EcosystemExplorer = () => {
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');

  const tick = useLiveTick();
  const items = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => [...getEcosystemItems()].sort((a, b) => a.displayOrder - b.displayOrder),
    [tick],
    []
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((item) => {
      const filterMatch = filter === 'all' || item.areas.includes(filter);
      return filterMatch && (!q || matchesQuery(item, q));
    });
  }, [items, filter, query]);

  const isFiltered = filter !== 'all' || query.trim() !== '';
  const clearFilters = () => {
    setFilter('all');
    setQuery('');
  };

  return (
    <section aria-labelledby="ecosystem-heading" data-testid="ecosystem-explorer" className="bg-mist py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl" data-testid="ecosystem-header">
          <p className="flex items-center gap-3">
            <span className="font-mono text-xs font-medium tracking-widest text-gold-700">01</span>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold-700">Amden Ecosystem</span>
          </p>
          <h1 id="ecosystem-heading" data-testid="ecosystem-heading" className="mt-4 text-balance text-3xl font-extrabold tracking-tight text-navy-900 sm:text-4xl lg:text-5xl">
            Every business solution, in one place
          </h1>
          <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
            Systems, productised services, training and partner solutions across operations, people,
            growth and business launch — discover the right support and the right way to buy it.
          </p>
        </div>

        <div className="mt-10 flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filter catalogue">
            {ECOSYSTEM_FILTERS.map((f) => {
              const count = f.key === 'all' ? items.length : items.filter((i) => i.areas.includes(f.key)).length;
              const isActive = filter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  aria-pressed={isActive}
                  data-testid={`eco-filter-${f.key}`}
                  onClick={() => setFilter(f.key)}
                  className={cn(
                    'rounded-full border-2 px-4 py-2 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-2',
                    isActive
                      ? 'border-navy-900 bg-navy-900 text-white'
                      : 'border-slate-200 bg-white text-navy-800 hover:border-navy-300 hover:bg-navy-50'
                  )}
                >
                  {f.label}
                  <span className={cn('ml-1.5 text-xs font-semibold', isActive ? 'text-slate-300' : 'text-slate-400')}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="relative w-full xl:max-w-xs">
            <label htmlFor="eco-search" className="sr-only">Search the catalogue</label>
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input
              id="eco-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search services, systems, training…"
              data-testid="eco-search"
              className="h-11 w-full rounded-md border border-slate-300 bg-white pl-10 pr-9 text-sm text-navy-900 shadow-sm transition-colors placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-1"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear search"
                data-testid="eco-search-clear"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 transition-colors hover:text-navy-900"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-4">
          <p aria-live="polite" className="text-sm font-semibold text-slate-600" data-testid="eco-results-count">
            Showing {filtered.length} of {items.length} solutions
          </p>
          {isFiltered && (
            <button
              type="button"
              onClick={clearFilters}
              data-testid="eco-clear-filters-btn"
              className="inline-flex items-center gap-1.5 text-sm font-bold text-gold-700 underline-offset-2 transition-colors hover:text-gold-700 hover:underline"
            >
              <X className="h-4 w-4" aria-hidden="true" />
              Clear Filters
            </button>
          )}
        </div>

        {filtered.length > 0 ? (
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="eco-grid">
            {filtered.map((item, i) => (
              <CatalogueCard key={item.id} item={item} index={i} />
            ))}
          </div>
        ) : (
          <div className="mt-8 rounded-2xl border-2 border-dashed border-slate-300 bg-white px-6 py-14 text-center" data-testid="eco-empty">
            <p className="text-lg font-extrabold text-navy-900">No matching solutions</p>
            <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-500">
              Try a different category or search term — or clear the filters to browse the full catalogue.
            </p>
            <button
              type="button"
              onClick={clearFilters}
              data-testid="eco-empty-clear-btn"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-navy-900 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-navy-700"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
